import { Component, OnInit } from '@angular/core';
import { IFeedConfiguration } from '../../Services/IFeedConfiguration';
import { TenantService } from '../../../../../Services/tenant.service';
import { FeedService } from '../../Services/feed.service';
import { FeedHistoryService } from '../../Services/feed-history.service';
import { FeedHistory } from '../../Services/feed-history.model';
import { JobLiveViewComponent } from '../../../../../Jobs/job.liveview.component';
import { EnvironmentService } from '@app/Environments';
import { NgClass, DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatDialog } from '@angular/material/dialog';
import { DialogResult, PopupDialog } from '@app/Common';
import { PermissionService } from '@app/Services';
import { Permissions } from '@app/Models/Permissions';
import { ToastMessageType, ToastService } from '@app/Common/toast';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-feed-configuration-summary',
    templateUrl: './feed-configuration-summary.component.html',
    styleUrls: ['./feed-configuration-summary.component.scss'],
    imports: [RouterLink, MatSlideToggle, MatButton, MatIcon, MatIconButton, NgClass, ExtendedModule, DatePipe, JobLiveViewComponent]
})
export class FeedConfigurationSummaryComponent implements OnInit {
    public feedConfigurations: IFeedConfiguration[];
    public itemIdSelectedForDeletion: string = null;
    /** Loaded history rows, keyed by feed configuration id. */
    public feedHistories: Record<string, FeedHistory[]> = {};
    public activeLiveViewJobId: string = null;
    private retryingHistoryIds = new Set<number>();

    constructor(
        private feedService: FeedService,
        private tenantService: TenantService,
        private feedHistoryService: FeedHistoryService,
        private environmentService: EnvironmentService,
        private readonly dialog: MatDialog,
        private readonly permissionService: PermissionService,
        private readonly toast: ToastService) {
    }

    public ngOnInit(): void {
        this.getFeedConfigurations();
    }

    public getFeedConfigurations(): void {
        this.feedService.getFeedConfigurationsWithoutDataFields(this.tenantService.selectedTenant.Value)
            .subscribe(feedConfigurations => {
                this.feedConfigurations = feedConfigurations.sort((a, b) => this.compareDates(a,b) ? -1 : 0);
            });
    }

    public getFeedUpdatedOn(feed: IFeedConfiguration): Date {
        return new Date(feed.UpdatedOn ? feed.UpdatedOn : feed.CreatedOn);
    }

    public setFeedConfigurationEnabled(feedConfigurationId: string, enabled: boolean): void {
        this.feedService
            .setFeedConfigurationEnabled(this.tenantService.selectedTenant.Value, feedConfigurationId, enabled)
            .subscribe();
    }

    public deleteButtonClicked(feedConfigurationId: string): void {
        this.itemIdSelectedForDeletion = null;
        this.feedService.deleteFeedConfiguration(this.tenantService.selectedTenant.Value, feedConfigurationId)
            .subscribe(() => this.getFeedConfigurations());
    }

    public selectItemForDeletion(itemId: string) {
        this.itemIdSelectedForDeletion = itemId;
    }

    public toggleHistory(feedConfigurationId: string): void {
        if(feedConfigurationId in this.feedHistories) {
            delete this.feedHistories[feedConfigurationId];
            return;
        }

        this.feedHistoryService.getFeedHistory(feedConfigurationId, this.tenantService.selectedTenant.Value)
        .subscribe((result: FeedHistory[]) => this.feedHistories[feedConfigurationId] = result);
    }

    public onHistoryKeyDown(event: KeyboardEvent, feedConfigurationId: string): void {
        if(event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        this.toggleHistory(feedConfigurationId);
    }

    public shouldHistoryBeDisplayed(feedConfigurationId: string): boolean {
        if(feedConfigurationId in this.feedHistories) {
            return true;
        }

        return false;
    }

    public existsHistory(feedConfigurationId: string): boolean {
        return this.feedHistories[feedConfigurationId] &&
            this.feedHistories[feedConfigurationId].length > 0;
    }

    public toggleJobLiveView(jobId: string): void {
        this.activeLiveViewJobId = this.activeLiveViewJobId === jobId ? null : jobId;
    }

    public confirmRetry(history: FeedHistory, feedConfigurationId: string): void {
        const dialogRef = this.dialog.open<PopupDialog, any, DialogResult>(PopupDialog);
        dialogRef.componentInstance.DialogTitle = 'Retry Feed Import';
        dialogRef.componentInstance.DialogDescription =
            `Are you sure you want to retry importing <b>${FeedConfigurationSummaryComponent.escapeHtml(history.FileName ?? 'this file')}</b>?<br><br>` +
            `This reprocesses the file that was previously imported, without re-adding it to FTP.`;
        dialogRef.componentInstance.DialogConfirmButtonText = 'Retry';
        dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

        dialogRef.afterClosed().subscribe((result) => {
            if (result !== undefined && result.Result) {
                this.retryFeedHistory(history, feedConfigurationId);
            }
        });
    }

    public isRetrying(historyId: number): boolean {
        return this.retryingHistoryIds.has(historyId);
    }

    public downloadFile(history: FeedHistory): void {
        this.feedHistoryService.downloadFile(history.Id, this.tenantService.selectedTenant.Value, history.FileName)
            .subscribe({
                error: (error: HttpErrorResponse) => this.toast.sendMessage(
                    'Download failed',
                    error?.status === 404
                        ? 'The feed file is no longer available.'
                        : 'The feed file could not be downloaded. Please try again later.',
                    ToastMessageType.error)
            });
    }

    public hasErrorReport(history: FeedHistory): boolean {
        return !!history.ReportDownloadUrl && history.StatusType !== 'Success';
    }

    public hasFeedFileDownloadPermission(): boolean {
        return this.permissionService.hasPermission(Permissions.feedFileDownload);
    }

    private retryFeedHistory(history: FeedHistory, feedConfigurationId: string): void {
        if (this.retryingHistoryIds.has(history.Id)) {
            return;
        }

        this.retryingHistoryIds.add(history.Id);
        this.feedHistoryService.retryFeedHistory(history.Id, this.tenantService.selectedTenant.Value)
            .subscribe({
                next: () => {
                    this.retryingHistoryIds.delete(history.Id);
                    this.toast.sendMessage('Retry started', 'The feed import has been queued.', ToastMessageType.success);
                    this.refreshHistory(feedConfigurationId);
                },
                error: (error: HttpErrorResponse) => {
                    this.retryingHistoryIds.delete(history.Id);
                    const message = typeof error?.error === 'string' && error.error
                        ? error.error
                        : 'This feed import could not be retried.';
                    this.toast.sendMessage('Retry failed', message, ToastMessageType.error);
                    this.refreshHistory(feedConfigurationId);
                }
            });
    }

    private static escapeHtml(value: string): string {
        // '&' must go first so the entities added below are not escaped again.
        return value
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#39;');
    }

    private refreshHistory(feedConfigurationId: string): void {
        this.feedHistoryService.getFeedHistory(feedConfigurationId, this.tenantService.selectedTenant.Value)
            .subscribe((result: FeedHistory[]) => this.feedHistories[feedConfigurationId] = result);
    }

    private compareDates(date1: IFeedConfiguration, date2: IFeedConfiguration): boolean {
        return new Date(date1.UpdatedOn ? date1.UpdatedOn : date1.CreatedOn) >
                    new Date(date2.UpdatedOn ? date2.UpdatedOn : date2.CreatedOn);
    }
}
